import { Image3D } from '~/domain/patient/image3d';

/**
 * A contiguous run of Z rows, in the units {@link ImageObject.uploadSlab}
 * takes: `[zStart, zStart + zCount)`.
 */
export type Slab = {
    zStart: number;
    zCount: number;
};

/**
 * The Z rows of a progressively loaded volume that already hold real pixels,
 * as a minimal set of contiguous slabs.
 *
 * Used when an {@link ImageObject} is created for a volume that is already
 * part-filled — typically just the anchor slice, but any number of slices may
 * have landed between the commit and the environment picking the image up.
 * Uploading these slabs and then each later arrival converges on exactly the
 * texture a single full-volume upload would have produced.
 *
 * Placement is read back through the volume's own grid — the same arithmetic
 * `Image3D.insertSlice` writes the rows with; `insertSlice` additionally
 * cross-checks that mapping against the manifest, so the two cannot disagree
 * about where a slice sits.
 *
 * Slabs are returned in ascending Z order, so a caller uploading them in
 * sequence walks the buffer forwards.
 */
export function arrivedSlabs(image: Image3D): Slab[] {
    const zIndices = new Set<number>();
    for (const slice of image.imageSlices) {
        const zIndex = Math.floor(
            image.grid.toVoxelSpace(slice.imagePosition)[2]
        );
        // A slice outside the volume cannot have been written into it; it
        // would also make the upload throw. Skipping is the safe reading:
        // worst case a row stays black until the volume is rebuilt.
        if (zIndex >= 0 && zIndex < image.grid.sizeZ) {
            zIndices.add(zIndex);
        }
    }

    return packRuns(zIndices);
}

/**
 * The Z rows of a progressively loaded volume whose slice will never arrive,
 * as a minimal set of contiguous slabs.
 *
 * These rows hold the zero the buffer was allocated with and always will, so
 * a viewer that distinguishes absent data from measured data needs them named
 * separately from the rows that are merely still in flight.
 *
 * Slabs are returned in ascending Z order.
 */
export function unavailableSlabs(image: Image3D): Slab[] {
    const zIndices = new Set<number>();
    for (const zIndex of image.unavailableSliceZIndices) {
        if (zIndex >= 0 && zIndex < image.grid.sizeZ) {
            zIndices.add(zIndex);
        }
    }
    return packRuns(zIndices);
}

/** Collapses Z indices into ascending runs of consecutive rows. */
function packRuns(zIndices: ReadonlySet<number>): Slab[] {
    const sorted = [...zIndices].sort((a, b) => a - b);
    const slabs: Slab[] = [];
    for (const zIndex of sorted) {
        const last = slabs[slabs.length - 1];
        if (last && last.zStart + last.zCount === zIndex) {
            last.zCount += 1;
            continue;
        }
        slabs.push({ zStart: zIndex, zCount: 1 });
    }
    return slabs;
}
